import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Loader2, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import type { VenueTable, VenueArea } from '@/types';

const tableSchema = z.object({
  tableNumber: z.string().min(1, 'Table number is required'),
  name: z.string().optional(),
  description: z.string().optional(),
  covers: z.coerce.number().int().min(1, 'Must have at least 1 cover'),
  minCovers: z.coerce.number().int().min(1).optional().or(z.literal('')),
  shape: z.enum(['square', 'round', 'rectangle']).default('square'),
  isActive: z.boolean().default(true),
});

const bulkSchema = z.object({
  startNumber: z.coerce.number().int().min(1, 'Start number required'),
  count: z.coerce.number().int().min(1).max(50, 'Max 50 tables at once'),
  covers: z.coerce.number().int().min(1).default(2),
  prefix: z.string().optional(),
  shape: z.enum(['square', 'round', 'rectangle']).default('square'),
});

type TableFormData = z.infer<typeof tableSchema>;
type BulkFormData = z.infer<typeof bulkSchema>;

export default function TablesConfig() {
  const { venueId, areaId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [editingTable, setEditingTable] = useState<VenueTable | null>(null);
  const [selectedTableIds, setSelectedTableIds] = useState<Set<string>>(new Set());

  const { data: area } = useQuery<VenueArea>({
    queryKey: ['venue-area', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}`);
      return response.data;
    },
  });

  const { data: tables, isLoading } = useQuery<VenueTable[]>({
    queryKey: ['venue-tables', venueId, areaId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas/${areaId}/tables`);
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TableFormData>({
    resolver: zodResolver(tableSchema),
    defaultValues: {
      tableNumber: '',
      name: '',
      description: '',
      covers: 2,
      minCovers: '',
      shape: 'square',
      isActive: true,
    },
  });

  const {
    register: registerBulk,
    handleSubmit: handleBulkSubmit,
    reset: resetBulk,
    formState: { errors: bulkErrors, isSubmitting: bulkSubmitting },
  } = useForm<BulkFormData>({
    resolver: zodResolver(bulkSchema),
    defaultValues: {
      startNumber: 1,
      count: 10,
      covers: 2,
      prefix: '',
      shape: 'square',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: TableFormData) => {
      const payload = {
        ...data,
        minCovers: data.minCovers || undefined,
        description: data.description || undefined,
      };
      await api.post(`/admin/venues/${venueId}/areas/${areaId}/tables`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-tables', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
      setShowModal(false);
      reset();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: TableFormData) => {
      const payload = {
        ...data,
        minCovers: data.minCovers || undefined,
        description: data.description || undefined,
      };
      await api.put(`/admin/venues/${venueId}/areas/${areaId}/tables/${editingTable?.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-tables', venueId, areaId] });
      setShowModal(false);
      setEditingTable(null);
      reset();
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async (data: BulkFormData) => {
      await api.post(`/admin/venues/${venueId}/areas/${areaId}/tables/bulk`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-tables', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
      setShowBulkModal(false);
      resetBulk();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (tableId: string) => {
      await api.delete(`/admin/venues/${venueId}/areas/${areaId}/tables/${tableId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-tables', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (tableIds: string[]) => {
      await api.post(`/admin/venues/${venueId}/areas/${areaId}/tables/bulk-delete`, { tableIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-tables', venueId, areaId] });
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
      setSelectedTableIds(new Set());
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (data: { tableIds: string[]; covers?: number; minCovers?: number | null; shape?: string; isActive?: boolean }) => {
      await api.post(`/admin/venues/${venueId}/areas/${areaId}/tables/bulk-update`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-tables', venueId, areaId] });
      setShowBulkEditModal(false);
      setSelectedTableIds(new Set());
    },
  });

  const toggleSelectTable = (tableId: string) => {
    setSelectedTableIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tableId)) {
        newSet.delete(tableId);
      } else {
        newSet.add(tableId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTableIds.size === tables?.length) {
      setSelectedTableIds(new Set());
    } else {
      setSelectedTableIds(new Set(tables?.map(t => t.id) || []));
    }
  };

  const handleBulkDelete = () => {
    if (selectedTableIds.size === 0) return;
    if (confirm(`Are you sure you want to delete ${selectedTableIds.size} tables? This action cannot be undone.`)) {
      bulkDeleteMutation.mutate(Array.from(selectedTableIds));
    }
  };

  const onSubmit = async (data: TableFormData) => {
    if (editingTable) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const openEditModal = (table: VenueTable) => {
    setEditingTable(table);
    reset({
      tableNumber: table.table_number,
      name: table.name || '',
      description: table.description || '',
      covers: table.covers,
      minCovers: table.min_covers || '',
      shape: table.shape,
      isActive: table.is_active,
    });
    setShowModal(true);
  };

  const activeTables = tables?.filter(t => t.is_active) || [];

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config/areas?type=${area?.booking_type_id || ''}`)}
          className="p-2 hover:bg-gray-100 rounded-md"
          title="Back to Areas"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Configure Tables</h1>
          <p className="text-sm text-gray-500">{area?.name}</p>
        </div>
        <button onClick={() => setShowBulkModal(true)} className="btn btn-secondary">
          <Plus className="h-4 w-4 mr-2" />
          Bulk Add
        </button>
        <button onClick={() => { setEditingTable(null); reset(); setShowModal(true); }} className="btn btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Add Table
        </button>
      </div>

      <div className="card">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Total tables: <span className="font-semibold">{activeTables.length}</span>
            {' | '}
            Total covers: <span className="font-semibold">{activeTables.reduce((sum, t) => sum + t.covers, 0)}</span>
          </p>
          {selectedTableIds.size > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{selectedTableIds.size} selected</span>
              <button
                onClick={() => setShowBulkEditModal(true)}
                className="btn btn-secondary btn-sm"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit Selected
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteMutation.isPending}
                className="btn btn-sm bg-red-600 text-white hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Selected
              </button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={tables && tables.length > 0 && selectedTableIds.size === tables.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Table #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Covers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shape</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-4 py-4"><div className="h-4 bg-gray-200 rounded w-16 ml-auto"></div></td>
                  </tr>
                ))
              ) : tables?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    No tables configured. Click "Add Table" or "Bulk Add" to add tables.
                  </td>
                </tr>
              ) : (
                tables?.map((table) => (
                  <tr key={table.id} className={`hover:bg-gray-50 ${!table.is_active ? 'opacity-50' : ''} ${selectedTableIds.has(table.id) ? 'bg-primary-50' : ''}`}>
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={selectedTableIds.has(table.id)}
                        onChange={() => toggleSelectTable(table.id)}
                      />
                    </td>
                    <td className="px-4 py-4 font-medium text-gray-900">{table.table_number}</td>
                    <td className="px-4 py-4 text-gray-500">{table.name || '-'}</td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {table.covers} {table.covers === 1 ? 'cover' : 'covers'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-500 capitalize">{table.shape}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        table.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {table.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(table)}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit Table"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this table? This action cannot be undone.')) {
                              deleteMutation.mutate(table.id);
                            }
                          }}
                          className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                          title="Delete Table"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Table Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTable ? 'Edit Table' : 'Add Table'}
              </h2>
              <button onClick={() => { setShowModal(false); setEditingTable(null); reset(); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">Table Number</label>
                <input type="text" className="input" placeholder="e.g., 1, A1, VIP-1" {...register('tableNumber')} />
                {errors.tableNumber && <p className="mt-1 text-sm text-red-600">{errors.tableNumber.message}</p>}
              </div>

              <div>
                <label className="label">Name (Optional)</label>
                <input type="text" className="input" placeholder="e.g., Window Table" {...register('name')} />
              </div>

              <div>
                <label className="label">Description (Optional)</label>
                <textarea
                  className="input"
                  placeholder="e.g., Located by the window with garden view"
                  rows={2}
                  {...register('description')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Covers</label>
                  <input type="number" className="input" min={1} {...register('covers')} />
                  {errors.covers && <p className="mt-1 text-sm text-red-600">{errors.covers.message}</p>}
                  <p className="mt-1 text-xs text-gray-500">Maximum seats at this table</p>
                </div>
                <div>
                  <label className="label">Minimum Covers</label>
                  <input type="number" className="input" min={1} placeholder="-" {...register('minCovers')} />
                  <p className="mt-1 text-xs text-gray-500">Minimum party size for this table</p>
                </div>
              </div>

              <div>
                <label className="label">Shape</label>
                <select className="input" {...register('shape')}>
                  <option value="square">Square</option>
                  <option value="round">Round</option>
                  <option value="rectangle">Rectangle</option>
                </select>
              </div>

              {editingTable && (
                <div className="flex items-center gap-3 pt-2">
                  <input
                    id="isActive"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    {...register('isActive')}
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Table is active and available for bookings
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); setEditingTable(null); reset(); }} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                  {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingTable ? 'Update' : 'Add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Bulk Add Tables</h2>
              <button onClick={() => { setShowBulkModal(false); resetBulk(); }} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleBulkSubmit((data) => bulkMutation.mutateAsync(data))} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Start Number</label>
                  <input type="number" className="input" min={1} {...registerBulk('startNumber')} />
                  {bulkErrors.startNumber && <p className="mt-1 text-sm text-red-600">{bulkErrors.startNumber.message}</p>}
                </div>
                <div>
                  <label className="label">Count</label>
                  <input type="number" className="input" min={1} max={50} {...registerBulk('count')} />
                  {bulkErrors.count && <p className="mt-1 text-sm text-red-600">{bulkErrors.count.message}</p>}
                </div>
              </div>

              <div>
                <label className="label">Prefix (Optional)</label>
                <input type="text" className="input" placeholder="e.g., T, A, VIP-" {...registerBulk('prefix')} />
                <p className="mt-1 text-xs text-gray-500">Tables will be named: prefix + number (e.g., T1, T2, T3...)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Covers per Table</label>
                  <input type="number" className="input" min={1} {...registerBulk('covers')} />
                </div>
                <div>
                  <label className="label">Shape</label>
                  <select className="input" {...registerBulk('shape')}>
                    <option value="square">Square</option>
                    <option value="round">Round</option>
                    <option value="rectangle">Rectangle</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowBulkModal(false); resetBulk(); }} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={bulkSubmitting} className="btn btn-primary">
                  {bulkSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Tables'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Edit {selectedTableIds.size} Tables
              </h2>
              <button onClick={() => setShowBulkEditModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const data: { tableIds: string[]; covers?: number; minCovers?: number | null; shape?: string; isActive?: boolean } = {
                  tableIds: Array.from(selectedTableIds),
                };
                const covers = formData.get('bulkCovers');
                const minCovers = formData.get('bulkMinCovers');
                const shape = formData.get('bulkShape');
                const isActive = formData.get('bulkIsActive');

                if (covers && covers !== '') data.covers = Number(covers);
                if (minCovers === '') data.minCovers = null;
                else if (minCovers) data.minCovers = Number(minCovers);
                if (shape && shape !== '') data.shape = shape as string;
                if (isActive !== null && isActive !== '') data.isActive = isActive === 'true';

                bulkUpdateMutation.mutate(data);
              }}
              className="space-y-4"
            >
              <p className="text-sm text-gray-500">
                Leave fields blank to keep current values. Only filled fields will be updated.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Covers</label>
                  <input type="number" name="bulkCovers" className="input" min={1} placeholder="Keep current" />
                </div>
                <div>
                  <label className="label">Min Covers</label>
                  <input type="number" name="bulkMinCovers" className="input" min={1} placeholder="Keep current" />
                </div>
              </div>

              <div>
                <label className="label">Shape</label>
                <select name="bulkShape" className="input" defaultValue="">
                  <option value="">Keep current</option>
                  <option value="square">Square</option>
                  <option value="round">Round</option>
                  <option value="rectangle">Rectangle</option>
                </select>
              </div>

              <div>
                <label className="label">Status</label>
                <select name="bulkIsActive" className="input" defaultValue="">
                  <option value="">Keep current</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowBulkEditModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={bulkUpdateMutation.isPending} className="btn btn-primary">
                  {bulkUpdateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Tables'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
